import type { Profesional } from '../../api/ops/citas';
import type { ReglaReserva } from '../../api/ops/disponibilidad';
import type { PreguntaServicio, ServicioCatalogo, TipoPregunta } from '../../api/ops/servicios';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';
import { aCentimos, dinero, textoDeCentimos } from '../../logica/dinero';

/**
 * Lógica de «Servicios» (9.4), tal cual el diseño (`diseno/citas/9.4-…html`): textos de duración, estado de cada servicio,
 * resúmenes de los desplegables y lo que se manda a la API. El panel nunca calcula precios: solo escribe los que da la API
 * («desde» si alguna pregunta cambia la duración o el precio).
 */

/** «1 h 30 min», «45 min». */
export function textoDuracion(minutos: number): string {
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return h > 0 ? `${String(h)} h${m > 0 ? ` ${String(m)} min` : ''}` : `${String(m)} min`;
}

/** Duraciones rápidas del selector (15 · 30 · 45 · 1 h · 1 h 30 · Otra). */
export const DURACIONES: readonly { minutos: number; texto: string }[] = [
    { minutos: 15, texto: '15 min' },
    { minutos: 30, texto: '30 min' },
    { minutos: 45, texto: '45 min' },
    { minutos: 60, texto: '1 h' },
    { minutos: 90, texto: '1 h 30' },
];

/** Colores en la agenda. */
export const COLORES: readonly { hex: string; nombre: string }[] = [
    { hex: '#C8102E', nombre: 'Rojo' },
    { hex: '#E07A5F', nombre: 'Coral' },
    { hex: '#D4A017', nombre: 'Mostaza' },
    { hex: '#5B8C5A', nombre: 'Salvia' },
    { hex: '#2A9D8F', nombre: 'Turquesa' },
    { hex: '#3D5A80', nombre: 'Azul' },
    { hex: '#7B5EA7', nombre: 'Lavanda' },
    { hex: '#1A1A1A', nombre: 'Negro' },
];

export function nombreColor(hex: string | undefined): string {
    return COLORES.find((c) => c.hex.toLowerCase() === (hex ?? '').toLowerCase())?.nombre ?? 'Sin color';
}

/** Tipos de pregunta con su icono. */
export const TIPOS_PREGUNTA: Readonly<Record<TipoPregunta, { texto: string; icono: NombreIcono }>> = {
    single_choice: { texto: 'Una respuesta', icono: 'circle-check' },
    multiple_choice: { texto: 'Varias respuestas', icono: 'list-checks' },
    number: { texto: 'Un número', icono: 'hash' },
    text: { texto: 'Texto libre', icono: 'type' },
};

/** «Lola, Elena y Marta». */
export function unir(nombres: readonly string[]): string {
    if (nombres.length <= 1) {
        return nombres[0] ?? '';
    }
    return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1] ?? ''}`;
}

export const primerNombre = (n: string): string => n.trim().split(/\s+/)[0] ?? n;

/** Profesionales activos que lo hacen. */
export function activosQueLoHacen(s: ServicioCatalogo, pros: readonly Profesional[]): Profesional[] {
    return pros.filter((p) => p.status === 'active' && s.professional_ids.includes(p.id));
}

/** Estado de la fila: Archivado · Sin profesional · Solo en el local · Activo. */
export function estadoServicio(s: ServicioCatalogo): { texto: string; chip: EstadoChip } {
    if (s.status === 'archived') {
        return { texto: 'Archivado', chip: 'neutral' };
    }
    if (!s.reservable) {
        return { texto: 'Sin profesional', chip: 'pending' };
    }
    if (!s.online_bookable) {
        return { texto: 'Solo en el local', chip: 'neutral' };
    }
    return { texto: 'Activo', chip: 'confirmed' };
}

/** «desde» cuando alguna pregunta (que no sea de texto) puede cambiar la duración o el precio. */
export function conDesde(s: ServicioCatalogo): boolean {
    return s.current_version.questions.some((q) => q.kind !== 'text');
}

/** «Color · 1 pregunta · versión 3». */
export function lineaServicio(s: ServicioCatalogo): string {
    const v = s.current_version;
    const n = v.questions.length;
    return [v.category ?? '', n > 0 ? `${String(n)} ${n === 1 ? 'pregunta' : 'preguntas'}` : '', `versión ${String(v.number)}`].filter((x) => x !== '').join(' · ');
}

/** Icono de la categoría (los del diseño; el resto, tijeras). */
export function iconoCategoria(categoria: string | undefined): NombreIcono {
    const c = (categoria ?? '').toLocaleLowerCase('es');
    if (c.includes('color') || c.includes('tinte')) {
        return 'palette';
    }
    if (c.includes('peinado')) {
        return 'star';
    }
    if (c.includes('uña') || c.includes('mani') || c.includes('pedi')) {
        return 'sparkles';
    }
    return 'scissors';
}

export type FiltroServicios = 'activos' | 'sin' | 'archivados';

export function filtrar(servicios: readonly ServicioCatalogo[], filtro: FiltroServicios, texto: string): ServicioCatalogo[] {
    const q = texto.trim().toLocaleLowerCase('es');
    return servicios
        .filter((s) => q === '' || s.current_version.name.toLocaleLowerCase('es').includes(q))
        .filter((s) => (filtro === 'archivados' ? s.status === 'archived' : filtro === 'sin' ? s.status === 'active' && !s.reservable : s.status === 'active'));
}

// ---- Cuándo se puede pedir cita (solo lectura aquí: la regla es de Disponibilidad, G244) ----

const textoMinutos = (m: number): string =>
    m >= 1440 ? (m === 1440 ? '1 día' : m % 10080 === 0 ? `${String(m / 10080)} semana${m > 10080 ? 's' : ''}` : `${String(Math.round(m / 1440))} días`) : m >= 60 ? (m === 60 ? '1 hora' : `${String(Math.round(m / 60))} horas`) : `${String(m)} minutos`;
const textoDias = (n: number): string => (n === 365 ? '1 año' : n === 730 ? '2 años' : `${String(n)} días`);

/** Por defecto en la regla del negocio (G256, G368). */
const POR_DEFECTO = { max: 90, min: 60, mismoDia: 'until_slot' as const, corte: 30, paso: 15 };

export interface ReglaVista {
    propia: boolean;
    resumen: string;
    frase: string;
    mismoDia: string;
    paso: string;
}

/** La regla que vale para el servicio: la suya (`reference` del servicio) o la del negocio, con los valores por defecto. */
export function reglaDelServicio(serviceId: string | null, reglas: readonly ReglaReserva[]): ReglaVista {
    const negocio = reglas.find((r) => r.target_kind === 'business');
    const propia = serviceId === null ? undefined : reglas.find((r) => r.target_kind === 'reference' && r.reference?.id === serviceId);
    const v = (k: 'max_advance_days' | 'min_advance_minutes' | 'same_day_cutoff_minutes' | 'slot_step_minutes'): number | null => propia?.[k] ?? negocio?.[k] ?? null;
    const max = v('max_advance_days') ?? POR_DEFECTO.max;
    const min = v('min_advance_minutes') ?? POR_DEFECTO.min;
    const modo = propia?.same_day_mode ?? negocio?.same_day_mode ?? POR_DEFECTO.mismoDia;
    const corte = v('same_day_cutoff_minutes') ?? POR_DEFECTO.corte;
    const paso = v('slot_step_minutes') ?? POR_DEFECTO.paso;
    const hasta = min > 0 ? `${textoMinutos(min)} antes` : 'el último momento';
    return {
        propia: propia !== undefined,
        resumen: `${propia !== undefined ? 'Propia' : 'Como todo el negocio'}: desde ${textoDias(max)} hasta ${hasta}`,
        frase: `Se puede pedir cita desde ${textoDias(max)} antes hasta ${min > 0 ? `${textoMinutos(min)} antes` : 'el último momento'}.`,
        mismoDia: modo === 'until_slot' ? 'El mismo día, hasta la hora de la cita.' : modo === 'minutes_before' ? `El mismo día, hasta ${textoMinutos(corte)} antes de la cita.` : 'El mismo día, solo antes de abrir.',
        paso: paso === 60 ? 'Cada hora' : `Cada ${String(paso)} minutos`,
    };
}

// ---- Formulario del servicio ----

export interface FormServicio {
    nombre: string;
    minutos: number;
    otra: boolean;
    precio: string;
    categoria: string;
    descripcion: string;
    color: string;
    enLinea: boolean;
    consentimiento: string;
    profesionales: string[];
}

export function formVacio(pros: readonly Profesional[]): FormServicio {
    return { nombre: '', minutos: 30, otra: false, precio: '', categoria: '', descripcion: '', color: '#C8102E', enLinea: true, consentimiento: '', profesionales: pros.filter((p) => p.status === 'active').map((p) => p.id) };
}

export function formDe(s: ServicioCatalogo): FormServicio {
    const v = s.current_version;
    return {
        nombre: v.name,
        minutos: v.base_duration_minutes,
        otra: !DURACIONES.some((d) => d.minutos === v.base_duration_minutes),
        precio: textoDeCentimos(v.base_price.amount),
        categoria: v.category ?? '',
        descripcion: v.description ?? '',
        color: v.color ?? '#C8102E',
        enLinea: s.online_bookable,
        consentimiento: v.consent_template_id ?? '',
        profesionales: [...s.professional_ids],
    };
}

export interface ErroresServicio {
    nombre?: string;
    precio?: string;
}

export function erroresServicio(f: FormServicio): ErroresServicio {
    const e: ErroresServicio = {};
    if (f.nombre.trim() === '') {
        e.nombre = 'Escribe el nombre del servicio.';
    }
    const c = aCentimos(f.precio);
    if (f.precio.trim() === '' || c === null || c < 0) {
        e.precio = 'Escribe el precio (0 si es gratis).';
    }
    return e;
}

/** Los campos de `ServiceFields` que salen del formulario (sin `policy_id`, que asigna Pagos). */
export function camposServicio(f: FormServicio, moneda: string, conFirma: boolean) {
    return {
        name: f.nombre.trim(),
        base_duration_minutes: f.minutos,
        base_price: { amount: aCentimos(f.precio) ?? 0, currency: moneda },
        ...(f.categoria.trim() === '' ? {} : { category: f.categoria.trim() }),
        description: f.descripcion.trim(),
        color: f.color,
        online_bookable: f.enLinea,
        ...(conFirma ? { consent_template_id: f.consentimiento === '' ? null : f.consentimiento } : {}),
    };
}

// ---- Preguntas ----

export interface OpcionForm {
    clave: string;
    id?: string;
    texto: string;
    minutos: string;
    euros: string;
}

export interface FormPregunta {
    id: string | null;
    tipo: TipoPregunta;
    texto: string;
    obligatoria: boolean;
    opciones: OpcionForm[];
    minimo: string;
    maximo: string;
    minutosUnidad: string;
    eurosUnidad: string;
    largo: string;
}

let contador = 0;
export const claveNueva = (): string => {
    contador += 1;
    return `n${String(contador)}`;
};

const euros = (c: number | undefined): string => textoDeCentimos(c ?? 0).replace(',00', '');

export function formPregunta(q: PreguntaServicio | null): FormPregunta {
    if (q === null) {
        return { id: null, tipo: 'single_choice', texto: '', obligatoria: false, opciones: [{ clave: claveNueva(), texto: '', minutos: '0', euros: '0' }], minimo: '1', maximo: '10', minutosUnidad: '0', eurosUnidad: '0', largo: '200' };
    }
    return {
        id: q.id,
        tipo: q.kind,
        texto: q.label,
        obligatoria: q.required,
        opciones: (q.options ?? []).map((o) => ({ clave: o.id, id: o.id, texto: o.label, minutos: String(o.minutes_delta), euros: euros(o.price_delta.amount) })),
        minimo: String(q.number_min ?? 0),
        maximo: String(q.number_max ?? 10),
        minutosUnidad: String(q.minutes_per_unit ?? 0),
        eurosUnidad: euros(q.price_per_unit?.amount),
        largo: String(q.text_max_length ?? 200),
    };
}

export interface ErroresPregunta {
    texto?: string;
    maximo?: string;
    opciones: Record<string, string>;
}

export function erroresPregunta(f: FormPregunta): ErroresPregunta {
    const e: ErroresPregunta = { opciones: {} };
    if (f.texto.trim() === '') {
        e.texto = 'Escribe la pregunta.';
    }
    if (f.tipo === 'number' && Number(f.maximo) < Number(f.minimo)) {
        e.maximo = 'El máximo no puede ser menor que el mínimo.';
    }
    if (f.tipo === 'single_choice' || f.tipo === 'multiple_choice') {
        for (const o of f.opciones) {
            if (o.texto.trim() === '') {
                e.opciones[o.clave] = 'Escribe la respuesta.';
            }
        }
    }
    return e;
}

export const sinErroresPregunta = (e: ErroresPregunta): boolean => e.texto === undefined && e.maximo === undefined && Object.keys(e.opciones).length === 0;

const entero = (t: string): number => {
    const n = Number.parseInt(t.trim().replace('−', '-'), 10);
    return Number.isNaN(n) ? 0 : n;
};
/** Céntimos de un «+/−» tecleado («-5», «−2,50», «10»); 0 si no es un importe. */
export function centimos(t: string): number {
    const limpio = t.trim().replace('−', '-');
    const negativo = limpio.startsWith('-');
    const c = aCentimos(negativo ? limpio.slice(1) : limpio.replace(/^\+/, '')) ?? 0;
    return negativo ? -c : c;
}

/** Lo que se manda a la API; las opciones que se quitan, con `retire` (no se borran). */
export function camposPregunta(f: FormPregunta, original: PreguntaServicio | null, moneda: string) {
    const base = { label: f.texto.trim(), required: f.obligatoria };
    if (f.tipo === 'number') {
        return { ...base, number_min: entero(f.minimo), number_max: entero(f.maximo), minutes_per_unit: entero(f.minutosUnidad), price_per_unit: { amount: centimos(f.eurosUnidad), currency: moneda } };
    }
    if (f.tipo === 'text') {
        return { ...base, text_max_length: Math.max(1, Math.min(500, entero(f.largo))) };
    }
    const quedan = new Set(f.opciones.map((o) => o.id).filter((x) => x !== undefined));
    const retiradas = (original?.options ?? []).filter((o) => !quedan.has(o.id)).map((o) => ({ id: o.id, label: o.label, minutes_delta: o.minutes_delta, retire: true }));
    return {
        ...base,
        options: [
            ...f.opciones.map((o, i) => ({ ...(o.id === undefined ? {} : { id: o.id }), label: o.texto.trim(), minutes_delta: entero(o.minutos), price_delta: { amount: centimos(o.euros), currency: moneda }, position: i, retire: false })),
            ...retiradas,
        ],
    };
}

/** Las líneas de una pregunta en la lista: cada respuesta con lo que suma. */
export function lineasPregunta(q: PreguntaServicio): { texto: string; cambio: string }[] {
    const signo = (n: number, sufijo: string) => `${n > 0 ? '+' : '−'}${String(Math.abs(n))}${sufijo}`;
    if (q.kind === 'number') {
        return [{ texto: `De ${String(q.number_min ?? 0)} a ${String(q.number_max ?? 0)}`, cambio: `${String(q.minutes_per_unit ?? 0)} min y ${dinero(q.price_per_unit ?? { amount: 0, currency: 'EUR' })} por unidad` }];
    }
    if (q.kind === 'text') {
        return [{ texto: `Hasta ${String(q.text_max_length ?? 0)} caracteres`, cambio: 'Sin cambio' }];
    }
    return (q.options ?? []).map((o) => {
        const partes = [o.minutes_delta !== 0 ? signo(o.minutes_delta, ' min') : '', o.price_delta.amount !== 0 ? `${o.price_delta.amount > 0 ? '+' : '−'}${dinero({ ...o.price_delta, amount: Math.abs(o.price_delta.amount) })}` : ''].filter((x) => x !== '');
        return { texto: o.label, cambio: partes.length === 0 ? 'Sin cambio' : partes.join(' · ') };
    });
}

/** «Así suma: 10 unidades = +60 min y +50,00 €.» (ejemplo con lo tecleado, no un precio de la API). */
export function ejemploUnidades(f: FormPregunta, moneda: string): string {
    const n = entero(f.minutosUnidad);
    const c = centimos(f.eurosUnidad);
    return `Así suma: 10 unidades = +${String(n * 10)} min y +${dinero({ amount: c * 10, currency: moneda })}.`;
}
